/**
 * @fileoverview Shared viewport bounding-box clamping for the fire hazard
 *               overlay (Phase 5.9). Extracted from mobile-pwa's original
 *               fireHazardOverlayService.ts so tenant-web and system-web's
 *               equivalent overlay services can enforce the identical
 *               client-side clamp before the server's bbox size cap
 *               rejects an oversized request.
 * @module core-domain/fireHazard/boundsClamp
 *
 * Responsibilities:
 * - Normalize a longitude value into the valid [-180, 180] range.
 * - Shrink an oversized viewport bounding box to a maximum span per side
 *   (matching each host API's FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES),
 *   keeping the same center, so a zoomed-out viewport still gets a
 *   (partial, centered) overlay instead of a 400.
 * - Export FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES as the single client-side
 *   copy of the bbox cap value, so it isn't re-declared as an independent
 *   literal per app (see FIXED GAP below).
 *
 * Dependencies:
 * - None (pure functions, no framework/app dependencies) — this is why the
 *   logic lives in core-domain rather than being duplicated per app.
 *
 * Architecture:
 * - Every app's fireHazardOverlayService.ts (mobile-pwa, tenant-web,
 *   system-web) calls clampBoundsToServerLimit before issuing the request,
 *   passing FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES (this file) as
 *   maxBboxDegrees. That value must match the server-side
 *   FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES in
 *   api/packages/core/src/services/fireHazardOverlayQueryService.ts — the
 *   two cannot share a single literal across the web/api package boundary
 *   today (api packages don't depend on web-side core-domain, and vice
 *   versa isn't established either), so keeping them in sync still
 *   requires a manual check when either one changes. Search both files by
 *   name if you change one.
 * - FIXED GAP: the clamping ALGORITHM (clampBoundsToServerLimit) was
 *   already consolidated here, but each app's fireHazardOverlayService.ts
 *   still independently re-declared the actual limit VALUE (10) as its
 *   own local literal, each with a comment saying it must match the
 *   server constant. That left the same "silently drift" risk the
 *   algorithm consolidation was meant to remove: bumping the server cap
 *   would leave clients clamping to the old (lower, harmless) value,
 *   but LOWERING the server cap would make every client 400 (not
 *   harmless) until each app's literal was separately updated. Exporting
 *   the value from here and having every app import it (rather than
 *   redeclare it) closes that gap on the CLIENT side — the api-package
 *   constant on the SERVER side is still a separate, manually-synced copy
 *   (see above).
 *
 * Notes:
 * - The clamped span is kept CLAMP_SAFETY_MARGIN_DEGREES under the cap
 *   (not exactly at it) to absorb floating-point rounding in the center ±
 *   half-width/height recomputation.
 */

/**
 * The single client-side copy of the fire-hazard overlay's bbox size cap
 * — import this rather than re-declaring a local literal (see this file's
 * Architecture note on the FIXED GAP). Must match
 * api/packages/core/src/services/fireHazardOverlayQueryService.ts's
 * server-side FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES.
 */
export const FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES = 10;

/**
 * @contract FireHazardOverlayBounds
 * Purpose: Viewport bounding box in decimal degrees (WGS84).
 * Fields:  west/south/east/north.
 * Sync:    N/A — request-scoped input, never persisted.
 */
export interface FireHazardOverlayBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

// Safety margin subtracted from the cap when clamping, to absorb
// floating-point rounding error. `centerLng + halfWidth` and
// `centerLng - halfWidth` are each rounded independently to the nearest
// double, so the resulting span can come out as e.g.
// 10.000000000000014 instead of exactly 10 for typical real-world
// viewport centers — which the server's inclusive `<= 10` check then
// rejects with a 400. 1e-6 degrees (~11cm at the equator) is many orders
// of magnitude larger than that rounding error and utterly negligible
// for map display, so it reliably keeps the clamped span strictly under
// the server's cap.
const CLAMP_SAFETY_MARGIN_DEGREES = 1e-6;

/**
 * Wraps a longitude into the valid [-180, 180] range. Mapbox GL's
 * map.getBounds() returns UNWRAPPED longitudes when the map has been
 * panned past the antimeridian (e.g. continuing east past 180 yields
 * values like 185 instead of wrapping to -175) — the server's bbox schema
 * validates west/east within [-180, 180] and rejects anything outside it,
 * so an unwrapped center computed from unwrapped bounds would still be out
 * of range after clamping otherwise.
 */
export function normalizeLongitude(lng: number): number {
  return ((lng + 180) % 360 + 360) % 360 - 180;
}

/**
 * @process clampBoundsToServerLimit
 * Purpose: shrink an oversized viewport bbox to maxBboxDegrees per side,
 *          keeping the same center, so a zoomed-out viewport still gets a
 *          (partial, centered) overlay instead of a 400.
 * Inputs:  bounds — the raw map viewport.
 *          maxBboxDegrees — the calling host API's bbox size cap (must
 *          match its server's FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES).
 * Outputs: bounds clamped to at most maxBboxDegrees wide/tall; unchanged
 *          if already within the limit. The clamped span is kept
 *          CLAMP_SAFETY_MARGIN_DEGREES under the cap — see that constant.
 * Idempotency: REQUIRED — pure function.
 */
export function clampBoundsToServerLimit(
  bounds: FireHazardOverlayBounds,
  maxBboxDegrees: number,
): FireHazardOverlayBounds {
  const width = bounds.east - bounds.west;
  const height = bounds.north - bounds.south;
  if (width <= maxBboxDegrees && height <= maxBboxDegrees) {
    return bounds;
  }
  const maxSafe = maxBboxDegrees - CLAMP_SAFETY_MARGIN_DEGREES;
  const centerLng = normalizeLongitude((bounds.east + bounds.west) / 2);
  const centerLat = (bounds.north + bounds.south) / 2;
  const halfWidth = Math.min(width, maxSafe) / 2;
  const halfHeight = Math.min(height, maxSafe) / 2;
  // A center near ±180 can still push west/east past the valid range even
  // after normalizing the center itself (e.g. center -178, halfWidth 5 =>
  // west -183). Rather than attempt antimeridian-crossing query splitting
  // (a much larger change for what is, for a US-focused product, a rare
  // edge case), clamp to the valid range — this shrinks the effective
  // viewport slightly near the dateline rather than returning a request
  // the server will 400.
  return {
    west: Math.max(-180, centerLng - halfWidth),
    east: Math.min(180, centerLng + halfWidth),
    south: Math.max(-90, centerLat - halfHeight),
    north: Math.min(90, centerLat + halfHeight),
  };
}
